import { useConnection } from 'wagmi';

import { usePointsLeaderboard } from '@/app/components/ReferralPageComponents/hooks/usePointsLeaderboard';
import ReferralEmptyState from '@/app/components/ReferralPageComponents/ReferralEmptyState';
import {
  formatLastUpdated,
  formatLeaderboardPointsLabel,
} from '@/app/components/ReferralPageComponents/referralFormatters';
import { cn } from '@/app/shared/lib/cn';
import { shortenAddress } from '@/app/shared/lib/formatters';
import PaginatedList from '@/app/shared/ui/PaginatedList';
import { Skeleton } from '@/app/shared/ui/Skeleton';

const HEADERS = [
  { id: 'rank', label: '#', className: '' },
  { id: 'address', label: 'Address', className: '' },
  { id: 'points', label: 'Points', className: 'text-right md:text-left' },
] as const;

const tableGrid = 'grid grid-cols-[45px_1fr_90px] md:grid-cols-[60px_1.2fr_1fr] items-center gap-4 px-4 py-2.5';

interface LeaderboardRowProps {
  rank: number | null;
  address: string;
  totalPoints: string;
  isYou?: boolean;
  explorerUrl?: string;
}

const LeaderboardRow = ({ rank, address, totalPoints, isYou, explorerUrl }: LeaderboardRowProps) => {
  const addressUrl = explorerUrl ? `${explorerUrl}/address/${address}` : undefined;
  const pointsLabel = formatLeaderboardPointsLabel(totalPoints, isYou);

  return (
    <div
      className={cn(
        'rounded-md font-normal hover:bg-elevated-card-hover',
        isYou ? 'bg-(--vault-info-bg)' : 'bg-elevated-card-2',
        tableGrid,
      )}
    >
      <span className='text-sm text-gray-200'>{rank ?? 'N/A'}</span>

      <span className='truncate text-sm text-gray-200'>
        {addressUrl ? (
          <a
            href={addressUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='hover:text-gray-300'
            onClick={(event) => event.stopPropagation()}
          >
            {shortenAddress(address)}
          </a>
        ) : (
          <span>{shortenAddress(address)}</span>
        )}
        {isYou && <span className='ml-1 text-gray-200'>(You)</span>}
      </span>

      <span className='text-right text-sm text-gray-200 md:text-left'>{pointsLabel}</span>
    </div>
  );
};

const PointsLeaderboardList = () => {
  const { address: connectedAddress, chain } = useConnection();
  const { rows, user, updatedAt, isLoading, isUnavailable } = usePointsLeaderboard();

  const explorerUrl = chain?.blockExplorers?.default?.url;
  const showUnavailable = !isLoading && (isUnavailable || rows.length === 0);

  const lastUpdatedLabel = updatedAt ? formatLastUpdated(updatedAt) : null;

  return (
    <div className='flex w-full flex-col md:pt-3.5'>
      <div className={cn('py-3', tableGrid)}>
        {HEADERS.map((header) => (
          <div key={header.id} className={cn('text-sm text-gray-500', header.className)}>
            {header.label}
          </div>
        ))}
      </div>

      <div className='flex flex-col gap-3.5'>
        {!showUnavailable && connectedAddress && (
          <>
            <Skeleton.Line loading={isLoading} className='h-10'>
              <LeaderboardRow
                rank={user?.rank ?? null}
                address={user?.address ?? connectedAddress}
                totalPoints={user?.totalPoints ?? '0'}
                explorerUrl={explorerUrl}
                isYou
              />
            </Skeleton.Line>
            <div className='h-px w-full bg-white/5' />
          </>
        )}

        {isLoading ? (
          <div className='flex flex-col gap-1.5'>
            {Array.from({ length: 10 }, (_, index) => (
              <Skeleton.Line key={index} loading className='h-10' />
            ))}
          </div>
        ) : showUnavailable ? (
          <ReferralEmptyState
            title='Unavailable'
            description='Leaderboard is currently unavailable. Your points are safe.'
          />
        ) : (
          <PaginatedList
            itemsPerPage={10}
            className='flex flex-col gap-1.5'
            afterListSlot={
              lastUpdatedLabel ? (
                <div className='mt-4 px-1 text-sm text-modal-result-body'>{lastUpdatedLabel}</div>
              ) : null
            }
          >
            {rows.map((row) => (
              <LeaderboardRow
                key={row.address}
                rank={row.rank}
                address={row.address}
                totalPoints={row.totalPoints}
                explorerUrl={explorerUrl}
              />
            ))}
          </PaginatedList>
        )}
      </div>
    </div>
  );
};

export default PointsLeaderboardList;
